import React from "react";

import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, Button, Stack, Typography } from "@mui/material";

import { useApiContext } from "../../../../api/context";
import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { useErrorSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { consoleErrorWithSentry } from "../../../../utils";
import useSegmentTrackEvent from "../../../../utils/useSegmentTrackEvent";
import { createCloudflowFromBlueprint } from "../../../Cloudflow/api";
import { type InsightCloudFlows } from "../../types";
import ActionCard from "./ActionCard";
import { CloudFlowDescription } from "./CloudFlowDescription";

type Props = {
  insightKey: string;
  insightBlueprintId: string;
  cloudFlows: InsightCloudFlows[];
};

const getCloudFlowChipColorByStatus = (status: string): "default" | "primary" => {
  switch (status) {
    case "Published":
      return "primary";
    case "Draft":
      return "default";
    default:
      return "default";
  }
};

export const CloudFlow: React.FC<Props> = ({ insightKey, insightBlueprintId, cloudFlows }) => {
  const api = useApiContext();
  const customerId = useCustomerId();
  const errorSnackbar = useErrorSnackbar();
  const { trackEvent } = useSegmentTrackEvent();

  const handleCreateClick = async () => {
    if (!insightBlueprintId) return;

    try {
      const { data } = await createCloudflowFromBlueprint(api, customerId, insightBlueprintId);

      trackEvent("Create CloudFlow Clicked", {
        insightKey,
        customerId,
      });

      // Open in a new tab
      window.open(`/customers/${customerId}/operate/cloudflow/edit/${data.id}`, "_blank");
    } catch (error) {
      consoleErrorWithSentry(error, {
        message: `Failed to create CloudFlow with blueprint id: ${insightBlueprintId}`,
      });

      errorSnackbar("Unable to create CloudFlow");
    }
  };

  const hasCloudFlows = cloudFlows?.length > 0;

  return (
    <Stack spacing={hasCloudFlows ? 1 : 0.5}>
      <Typography variant="body1" sx={{ pt: 1, fontWeight: 500 }}>
        {hasCloudFlows ? `CloudFlows (${cloudFlows.length})` : "Automate this insight"}
      </Typography>
      {hasCloudFlows ? (
        <Stack spacing={1}>
          {cloudFlows.map((cloudFlow) => (
            <ActionCard
              key={cloudFlow.id}
              title={cloudFlow.name}
              status={cloudFlow.status}
              date={cloudFlow.createdAt}
              url={`/customers/${customerId}/operate/cloudflow/edit/${cloudFlow.id}`}
              chipColor={getCloudFlowChipColorByStatus(cloudFlow.status)}
            />
          ))}
        </Stack>
      ) : (
        <Box sx={{ pb: 0.5 }}>
          <CloudFlowDescription />
        </Box>
      )}
      <Button
        variant="outlined"
        size="medium"
        endIcon={hasCloudFlows ? <OpenInNew /> : undefined}
        onClick={handleCreateClick}
        fullWidth
      >
        Create a CloudFlow
      </Button>
    </Stack>
  );
};
